/* OpponentsPanel — Scheduler / AD ▸ Opponents (AJ 2026-10-13).
 *
 * A friendlier face on the SAME data as WP Mgmt ▸ Teams and ▸ Venues: every
 * cell writes through /api/ol-canonical/{teams|venues} (same adapter, same WP
 * push, same canonical slugs built by OL — users never see or type a slug).
 * Logos go through the shared logo path (upload-classified → PATCH logo_url →
 * services/team_logo + ensure_wp_logo), so the OL UI, the WP site and the
 * Sabres App all change together. Doc: /app/docs/OPPONENTS_UI.md.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Loader2, MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';
import axios from 'axios';
import bridgeApi from '../admin/bridgeApi';
import { ENTITIES } from '../admin/entityConfigs';
import EntityForm from '../admin/EntityForm';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useTheme } from '../../contexts/ThemeContext';
import AddTeamModal, { saveTeam } from './AddTeamModal';
import AddVenueModal from './AddVenueModal';

const API = process.env.REACT_APP_BACKEND_URL;
const ADD_VENUE = '__add_venue__';
const errMsg = (e, fb) => e?.response?.data?.detail || e?.message || fb;
/** Logos are stored root-relative (/api/wp-media/file/…) — prefix the API host. */
export const logoSrc = (u) => (!u ? '' : u.startsWith('/api/') ? `${API || ''}${u}` : u);
const normIdent = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
/** Our own (org) team rows — hidden from Opponents (they stay in the data / WP Mgmt).
 *  DB-derived via /own-identity (org name/mascot + own squad labels): an own-squad
 *  code, an exact own label, an own label + variant ("Varsity Girls White"), or a
 *  title carrying the org's one-word identity ("All Sabres Teams"). No hardcoded names. */
export function isOwnTeam(t, ident) {
  if (t.code) return true;
  const n = normIdent(t.title || t.name);
  if (!n) return false;
  if (ident.has(n)) return true;
  const words = n.split(' ');
  const noSport = !t.sport_id || String(t.sport_id) === '-1';
  for (const id of ident) {
    if (id.includes(' ')) { if (n.startsWith(`${id} `)) return true; }   // own label + variant
    // one-word org identity ("sabres") only on sport-less legacy rows ("All Sabres Teams");
    // a real opponent with a sport ("Raleigh Sabres") stays listed
    else if (noSport && words.includes(id)) return true;
  }
  return false;
}
const OPP_TYPES = [['', '—'], ['homeschool', 'Homeschool'], ['private', 'Private'], ['nationals', 'Nationals'],
  ['public', 'Public'], ['other', 'Other']];

export default function OpponentsPanel({ orgId }) {
  const { isDark } = useTheme();
  const [tab, setTab] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [sports, setSports] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [venueFor, setVenueFor] = useState(null);   // team row waiting for a new venue
  const [homeIds, setHomeIds] = useState(new Set());
  const [ownIdent, setOwnIdent] = useState(new Set());

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      bridgeApi.invalidateEntity('/teams');
      bridgeApi.invalidateEntity('/venues');
      const [t, s, v, h, own] = await Promise.all([
        bridgeApi.get('/teams', orgId, { limit: 2000 }),
        bridgeApi.get('/sports', orgId, { limit: 200 }),
        bridgeApi.get('/venues', orgId, { limit: 2000 }),
        axios.get(`${API}/api/ad-teams/${orgId}/home-venues`).catch(() => null),
        axios.get(`${API}/api/ol-canonical/own-identity/${orgId}`).catch(() => null),
      ]);
      setOwnIdent(new Set(own?.data?.data || []));
      setTeams(Array.isArray(t?.data) ? t.data : []);
      setSports(Array.isArray(s?.data) ? s.data : []);
      setVenues(Array.isArray(v?.data) ? v.data : []);
      // Org home venues live in Org Teams ▸ Venues — hidden from Opponents ▸ Venues.
      setHomeIds(new Set((h?.data?.data || []).filter((x) => x.is_home).map((x) => x.id)));
    } finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const sportName = (id) => sports.find((s) => s.ol_id === id || String(s.id) === String(id))?.title || '';
  const rows = useMemo(() => {
    const k = q.trim().toLowerCase();
    return teams
      .filter((t) => !isOwnTeam(t, ownIdent))   // our own (org) teams are set up by OL from Org Teams — not listed here
      .filter((t) => !k || `${t.title} ${t.short_name}`.toLowerCase().includes(k))
      .filter((t) => !sportFilter || t.sport_id === sportFilter || (t.other_sport_ids || []).includes(sportFilter))
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [teams, q, sportFilter, ownIdent]);

  const patchTeam = async (t, body) => {
    setBusy(t.ol_id || t.id);
    try {
      const res = await bridgeApi.patch('/teams', orgId, t.ol_id || t.id, body);
      const row = res?.data || {};
      setTeams((list) => list.map((x) => ((x.ol_id || x.id) === (t.ol_id || t.id) ? { ...x, ...row } : x)));
    } catch (e) { alert(errMsg(e, 'Save failed')); }
    finally { setBusy(null); }
  };
  const setType = async (t, v) => {
    try {
      const m = t._ol_meta || {};
      await axios.put(`${API}/api/team-meta/${orgId}/${t.id}`,
        { opponent_type: v, is_conference: !!m.is_conference, notes: m.notes || '' });
      setTeams((list) => list.map((x) => (x === t ? { ...x, _ol_meta: { ...(x._ol_meta || {}), opponent_type: v } } : x)));
    } catch (e) { alert(errMsg(e, 'Save failed')); }
  };
  const uploadLogo = async (t, file) => {
    setBusy(t.ol_id || t.id);
    try {
      const d = await bridgeApi.mediaUploadClassified(orgId, file, 'logo', { owner: t.slug || t.ol_id }, `${t.title} logo`);
      if (!d?.ok) throw new Error(d?.error?.message || 'Upload failed');
      await patchTeam(t, { logo_url: `/api/wp-media/file/${orgId}/${d.data.attachment_id}` });
    } catch (e) { alert(errMsg(e, 'Logo upload failed')); setBusy(null); }
  };
  const removeLogo = (t) => {
    if (window.confirm(`Remove the logo for ${t.title}? It is removed from the website and the Sabres app too.`)) {
      patchTeam(t, { logo_url: '' });
    }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete ${t.title}?`)) return;
    try { await bridgeApi.del('/teams', orgId, t.ol_id || t.id); setTeams((l) => l.filter((x) => x !== t)); }
    catch (e) { alert(errMsg(e, 'Delete failed')); }
  };

  const th = `px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  const cell = `w-full bg-transparent rounded px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  const sel = `${cell} cursor-pointer`;
  const card = `rounded-lg border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`;
  const text = (t, key, ph) => (
    <input defaultValue={t[key] || ''} key={`${t.ol_id}-${key}-${t[key]}`} placeholder={ph} className={cell}
      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (t[key] || '') && (key !== 'title' || v)) patchTeam(t, { [key]: v }); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={`opp-${key}-${t.ol_id}`} />
  );

  if (!orgId) return null;
  return (
    <div className="space-y-3" data-testid="opponents-panel">
      <div className="flex items-center gap-2">
        {[['teams', 'Opponents'], ['venues', 'Venues']].map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? 'default' : 'outline'} onClick={() => setTab(k)}
            className={tab === k ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''} data-testid={`opp-subtab-${k}`}>{l}</Button>
        ))}
      </div>

      {tab === 'venues' ? (
        <VenuesTable orgId={orgId} venues={venues.filter((v) => !homeIds.has(v.ol_id))} reload={load} isDark={isDark} th={th} cell={cell} card={card} />
      ) : (
      <div className={`${card} p-3 space-y-2`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Teams</span>
          <span className="text-xs text-slate-500">{loading ? '—' : `${rows.length} of ${teams.filter((t) => !isOwnTeam(t, ownIdent)).length}`}</span>
          <div className="relative ml-auto">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a team…"
              className={`pl-7 pr-2 py-1 text-xs rounded border w-48 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
              data-testid="opp-search" />
          </div>
          <select value={sportFilter} onChange={(e) => setSportFilter(e.target.value)} data-testid="opp-sport-filter"
            className={`py-1 px-2 text-xs rounded border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}>
            <option value="">All sports</option>
            {sports.map((s) => <option key={s.ol_id} value={s.ol_id}>{s.title}</option>)}
          </select>
          <Button size="sm" onClick={() => setAdding(true)} className="bg-amber-600 hover:bg-amber-700 text-white h-8"
            data-testid="opp-add-btn"><Plus className="h-4 w-4 mr-1" />Add team</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
        ) : (
        <div className={`overflow-x-auto rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <table className="w-full text-xs" data-testid="opp-table">
            <thead className={isDark ? 'bg-slate-900/60' : 'bg-slate-50'}>
              <tr>
                <th className={th}>Logo</th><th className={th}>Team</th><th className={th}>Short</th><th className={th}>Sport</th>
                <th className={th} title="Other sports this same team plays">Also plays</th><th className={th}>Home venue</th>
                <th className={th}>Type</th><th className={th}>Website</th><th className={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const id = t.ol_id || t.id;
                const others = t.other_sport_ids || [];
                return (
                  <tr key={id} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`} data-testid={`opp-row-${id}`}>
                    <td className="px-2 py-1">
                      <LogoCell t={t} busy={busy === id} isDark={isDark} onFile={(f) => uploadLogo(t, f)} onRemove={() => removeLogo(t)} />
                    </td>
                    <td className="px-1 py-1 min-w-[170px]">
                      <div className="flex items-center gap-1">
                        {text(t, 'title', 'Team name')}
                      </div>
                    </td>
                    <td className="px-1 py-1 min-w-[90px]">{text(t, 'short_name', '')}</td>
                    <td className="px-1 py-1 min-w-[100px]">
                      <select value={t.sport_id || ''} className={sel} onChange={(e) => patchTeam(t, { sport_id: e.target.value })}
                        data-testid={`opp-sport-${id}`}>
                        <option value="">—</option>
                        {sports.map((s) => <option key={s.ol_id} value={s.ol_id}>{s.title}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1 whitespace-nowrap">
                      {sports.filter((s) => s.ol_id !== t.sport_id).map((s) => {
                        const on = others.includes(s.ol_id);
                        return (
                          <button key={s.ol_id} type="button" data-testid={`opp-also-${id}-${s.ol_id}`}
                            onClick={() => patchTeam(t, { other_sport_ids: on ? others.filter((x) => x !== s.ol_id) : [...others, s.ol_id] })}
                            className={`mr-1 px-1.5 py-0.5 rounded-full border text-[10px] ${on ? 'bg-teal-600 border-teal-600 text-white' : isDark ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
                            {s.title}
                          </button>
                        );
                      })}
                    </td>
                    <td className="px-1 py-1 min-w-[150px]">
                      <select value={t.home_venue_id || ''} className={sel} data-testid={`opp-venue-${id}`}
                        onChange={(e) => (e.target.value === ADD_VENUE ? setVenueFor(t) : patchTeam(t, { home_venue_id: e.target.value }))}>
                        <option value="">—</option>
                        <option value={ADD_VENUE}>+ Add venue…</option>
                        {venues.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                          .map((v) => <option key={v.ol_id} value={v.ol_id}>{v.title}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1 min-w-[95px]">
                      <select value={t._ol_meta?.opponent_type || ''} className={sel} onChange={(e) => setType(t, e.target.value)}
                        disabled={!t.wp_synced} title={t.wp_synced ? '' : 'Available once the team is on the website'}
                        data-testid={`opp-type-${id}`}>
                        {OPP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1 min-w-[140px]">{text(t, 'link', 'https://')}</td>
                    <td className="px-1 py-1 whitespace-nowrap">
                      <button type="button" title="All fields (alt logo, mascots, notes)" onClick={() => setEditing(t)}
                        className="p-1 text-slate-400 hover:text-amber-500" data-testid={`opp-more-${id}`}><MoreHorizontal className="h-3.5 w-3.5" /></button>
                      <button type="button" title="Delete team" onClick={() => del(t)}
                        className="p-1 text-red-400 hover:text-red-600" data-testid={`opp-delete-${id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        <p className="text-[10px] text-slate-500">
          Same teams as WP Mgmt ▸ Teams — changes show in both places, on the website and in the Sabres app.
          Our own teams are set up in Org Teams; our home venues in Org Teams ▸ Venues.
        </p>
      </div>
      )}

      <AddTeamModal open={adding} orgId={orgId} onClose={() => setAdding(false)} onCreated={() => load()} />
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100">
          <DialogTitle className="sr-only">Edit team</DialogTitle>
          {editing && (
            <EntityForm entityConfig={ENTITIES.teams} initialValues={editing} entities={{ sports, venues }}
              entityKey="teams" orgId={orgId} onCancel={() => setEditing(null)}
              onSave={async (vals) => {
                try { await saveTeam(orgId, { ...vals, id: editing.ol_id || editing.id }); setEditing(null); load(); }
                catch (e) { alert(errMsg(e, 'Save failed')); }
              }} />
          )}
        </DialogContent>
      </Dialog>
      <AddVenueModal open={!!venueFor} orgId={orgId} onClose={() => setVenueFor(null)}
        onCreated={async (v) => {
          const t = venueFor;
          setVenueFor(null);
          setVenues((l) => [...l, v]);
          if (t && v?.ol_id) await patchTeam(t, { home_venue_id: v.ol_id });
        }} />
    </div>
  );
}

function LogoCell({ t, busy, isDark, onFile, onRemove }) {
  const ref = useRef(null);
  const src = logoSrc(t.logo_url);
  return (
    <div className="relative group h-8 w-12" data-testid={`opp-logo-${t.ol_id}`}>
      {src ? (
        <img src={src} alt="" className="h-8 w-12 object-contain bg-white rounded border border-slate-200"
          onError={(e) => { e.target.style.visibility = 'hidden'; }} />
      ) : (
        <div className={`h-8 w-12 rounded border border-dashed ${isDark ? 'border-slate-600' : 'border-slate-300'}`} />
      )}
      {busy ? (
        <div className="absolute inset-0 rounded bg-black/40 flex items-center justify-center"><Loader2 className="h-3.5 w-3.5 animate-spin text-white" /></div>
      ) : (
        <div className="absolute inset-0 rounded bg-black/55 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1">
          <button type="button" title={src ? 'Replace logo' : 'Add logo'} onClick={() => ref.current?.click()}
            className="p-0.5 rounded bg-white/90 text-slate-800" data-testid={`opp-logo-upload-${t.ol_id}`}><ImagePlus className="h-3 w-3" /></button>
          {t.logo_url && (
            <button type="button" title="Remove logo" onClick={onRemove}
              className="p-0.5 rounded bg-white/90 text-red-600" data-testid={`opp-logo-remove-${t.ol_id}`}><Trash2 className="h-3 w-3" /></button>
          )}
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </div>
  );
}

function VenuesTable({ orgId, venues, reload, isDark, th, cell, card }) {
  const [adding, setAdding] = useState(false);
  const [rows, setRows] = useState(venues);
  useEffect(() => setRows(venues), [venues]);
  const patch = async (v, body) => {
    try {
      const res = await bridgeApi.patch('/venues', orgId, v.ol_id || v.id, body);
      setRows((l) => l.map((x) => (x === v ? { ...x, ...(res?.data || body) } : x)));
    } catch (e) { alert(errMsg(e, 'Save failed')); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete venue ${v.title}?`)) return;
    try { await bridgeApi.del('/venues', orgId, v.ol_id || v.id); setRows((l) => l.filter((x) => x !== v)); }
    catch (e) { alert(errMsg(e, 'Delete failed')); }
  };
  const text = (v, key, ph, w) => (
    <td className={`px-1 py-1 ${w || ''}`}>
      <input defaultValue={v[key] || ''} key={`${v.ol_id}-${key}-${v[key]}`} placeholder={ph} className={cell}
        onBlur={(e) => { const nv = e.target.value.trim(); if (nv !== (v[key] || '') && (key !== 'title' || nv)) patch(v, { [key]: nv }); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={`venue-${key}-${v.ol_id}`} />
    </td>
  );
  const sorted = rows.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  return (
    <div className={`${card} p-3 space-y-2`}>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Venues</span>
        <span className="text-xs text-slate-500">{rows.length}</span>
        <Button size="sm" onClick={() => setAdding(true)} className="ml-auto bg-amber-600 hover:bg-amber-700 text-white h-8"
          data-testid="venue-add-btn"><Plus className="h-4 w-4 mr-1" />Add venue</Button>
      </div>
      <div className={`overflow-x-auto rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        <table className="w-full text-xs" data-testid="venue-table">
          <thead className={isDark ? 'bg-slate-900/60' : 'bg-slate-50'}>
            <tr><th className={th}>Venue</th><th className={th}>Street</th><th className={th}>City</th><th className={th}>State</th>
              <th className={th}>Zip</th><th className={th}>Map link</th><th className={th} /></tr>
          </thead>
          <tbody>
            {sorted.map((v) => (
              <tr key={v.ol_id || v.id} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                {text(v, 'title', 'Name', 'min-w-[180px]')}{text(v, 'address_1', 'Street', 'min-w-[160px]')}
                {text(v, 'city', 'City', 'min-w-[100px]')}{text(v, 'state', 'ST', 'w-14')}{text(v, 'zip', 'Zip', 'w-20')}
                {text(v, 'map_url', 'https://maps…', 'min-w-[140px]')}
                <td className="px-1 py-1">
                  <button type="button" onClick={() => del(v)} className="p-1 text-red-400 hover:text-red-600" title="Delete venue"
                    data-testid={`venue-delete-${v.ol_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddVenueModal open={adding} orgId={orgId} onClose={() => setAdding(false)} onCreated={() => reload()} />
    </div>
  );
}
